import React from "react";
import { useLocation } from "react-router-dom";
import { SITE, ADMIN_PORTAL_LOGIN_URL } from "@/lib/site";
import { Menu, X } from "lucide-react";

export default function Navbar() {
  const location = useLocation();
  const [open, setOpen] = React.useState(false);
  const [scrolled, setScrolled] = React.useState(false);

  React.useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // On a policy page the sections aren't mounted, so section anchors have to
  // point back at the landing page rather than at a local fragment.
  const onLanding = location.pathname === "/";
  const anchor = (id) => (onLanding ? `#${id}` : `/#${id}`);

  const links = [
    { href: anchor("about"), label: "About" },
    { href: anchor("services"), label: "Services" },
    { href: anchor("why"), label: "Why Wateriia" },
    { href: anchor("app"), label: "App" },
    { href: anchor("partner"), label: "Partner" },
    { href: anchor("contact"), label: "Contact" },
    { href: ADMIN_PORTAL_LOGIN_URL, label: "Admin" },
  ];

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled ? "py-3" : "py-5"
      }`}
    >
      <div className="container-x">
        <div
          className={`flex items-center justify-between rounded-full px-4 md:px-6 py-2.5 transition-all duration-300 ${
            scrolled ? "glass shadow-[0_8px_30px_-10px_rgba(2,32,71,0.18)]" : "bg-transparent"
          }`}
        >
          <a href={onLanding ? "#top" : "/"} data-testid="navbar-brand" className="flex items-center gap-2.5">
            <img src={SITE.logo} alt="Wateriia" className="h-9 w-9 object-contain" />
            <div className="leading-none">
              <div className="font-bold tracking-tight text-[15px]">{SITE.brand}<span className="text-[#0369a1]">.</span></div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 -mt-0.5">{SITE.city} · {SITE.country}</div>
            </div>
          </a>

          <nav className="hidden lg:flex items-center gap-7 text-sm">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className="link-x text-slate-700 hover:text-[#0369a1] transition-colors"
              >
                {l.label}
              </a>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-2">
            <a
              href={anchor("contact")}
              data-testid="navbar-cta-contact"
              className="px-4 py-2 rounded-full text-sm font-medium text-[#0b1d2a] hover:bg-white/60 transition"
            >
              Contact
            </a>
            <a
              href={anchor("partner")}
              data-testid="navbar-cta-partner"
              className="px-4 py-2 rounded-full text-sm font-semibold bg-[#0b1d2a] text-white hover:bg-[#0369a1] transition"
            >
              Partner With Us
            </a>
          </div>

          <button
            data-testid="navbar-mobile-toggle"
            className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-full glass"
            onClick={() => setOpen((s) => !s)}
            aria-label="Menu"
          >
            {open ? <X size={18} /> : <Menu size={18} />}
          </button>
        </div>

        {open && (
          <div data-testid="navbar-mobile-menu" className="lg:hidden mt-2 glass rounded-2xl p-4 fade-up">
            <div className="grid gap-2">
              {links.map((l) => (
                <a
                  key={l.href}
                  href={l.href}
                  data-testid={`mnav-link-${l.label.toLowerCase()}`}
                  onClick={() => setOpen(false)}
                  className="px-3 py-2 rounded-lg text-slate-700 hover:bg-white"
                >
                  {l.label}
                </a>
              ))}
              <a
                href={anchor("partner")}
                onClick={() => setOpen(false)}
                data-testid="mnav-cta-partner"
                className="mt-2 text-center px-4 py-2.5 rounded-full text-sm font-semibold bg-[#0b1d2a] text-white"
              >
                Partner With Us
              </a>
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
